/**
 * The app's icons, drawn.
 *
 * Not emoji and not glyphs. `☀`, `◉` and `🗓` came out as whatever each platform had
 * to hand — a replacement box where no emoji font is installed, a clipped ring in
 * Inter, a different weight and baseline on every machine — because a text glyph is
 * typeset, not laid out: it carries its own metrics and cannot be aligned or sized.
 *
 * These take their colour from `currentColor` and their size from the font size, so
 * a caller styles them the way it styles its text and nothing needs a per-icon rule.
 */
import type { SVGProps } from 'react';

const base = {
  width: '1em',
  height: '1em',
  viewBox: '0 0 16 16',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.5,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
  focusable: 'false',
} as const;

type IconProps = Omit<SVGProps<SVGSVGElement>, 'viewBox' | 'children'>;

export const CalendarIcon = (props: IconProps) => (
  <svg {...base} {...props}>
    <rect x="2" y="3.25" width="12" height="11" rx="2" />
    <path d="M2 6.75h12M5.25 1.75v2.5M10.75 1.75v2.5" />
  </svg>
);

export const SunIcon = (props: IconProps) => (
  <svg {...base} {...props}>
    <circle cx="8" cy="8" r="3.15" />
    <path d="M8 1.4v1.5M8 13.1v1.5M1.4 8h1.5M13.1 8h1.5M3.4 3.4l1.05 1.05M11.55 11.55l1.05 1.05M12.6 3.4l-1.05 1.05M4.45 11.55L3.4 12.6" />
  </svg>
);

export const MoonIcon = (props: IconProps) => (
  <svg {...base} {...props}>
    {/* A crescent as one closed path, so it reads at 14px instead of turning into a
        grey blob the way a circle-minus-circle does. */}
    <path d="M13.2 10.15A5.6 5.6 0 0 1 5.85 2.8a5.85 5.85 0 1 0 7.35 7.35Z" />
  </svg>
);

/**
 * The dropdown caret. The native selects draw theirs in CSS and cannot use this;
 * this one exists so every CUSTOM dropdown trigger shows the same chevron at the
 * same weight, instead of the `▾` glyph — which is typeset, not laid out, and came
 * out a different size beside every font it met.
 */
export const ChevronDownIcon = (props: IconProps) => (
  <svg {...base} {...props}>
    <path d="M4 6.25l4 3.5 4-3.5" />
  </svg>
);

/**
 * The three states of a sortable column head.
 *
 * `▲`, `▼` and `⇅` were doing this job, and `⇅` is the reason they had to stop: it
 * is a different glyph from the other two — a different weight, a different width
 * and a different baseline in every font — so an unsorted column's marker sat
 * visibly off from the sorted one's. Drawn, all three are the same two chevrons at
 * the same stroke, and the idle state is plainly the pair of them.
 */
export const SortAscIcon = (props: IconProps) => (
  <svg {...base} {...props}>
    <path d="M4 9.75l4-4 4 4" />
  </svg>
);

export const SortDescIcon = (props: IconProps) => (
  <svg {...base} {...props}>
    <path d="M4 6.25l4 4 4-4" />
  </svg>
);

export const SortIdleIcon = (props: IconProps) => (
  <svg {...base} {...props}>
    <path d="M4.75 6.75L8 3.5l3.25 3.25" />
    <path d="M4.75 9.25L8 12.5l3.25-3.25" />
  </svg>
);

/** The tick in a multi-select's option, drawn so it sits the same in every font. */
export const CheckIcon = (props: IconProps) => (
  <svg {...base} {...props}>
    <path d="M3.5 8.25l3 3 6-6.5" />
  </svg>
);

export const InfoIcon = (props: IconProps) => (
  <svg {...base} {...props}>
    <circle cx="8" cy="8" r="6.25" />
    <path d="M8 7.25v4" />
    <path d="M8 4.75h.01" />
  </svg>
);

export const MenuIcon = (props: IconProps) => (
  <svg {...base} {...props}>
    <path d="M2.5 4.5h11M2.5 8h11M2.5 11.5h11" />
  </svg>
);

export const CloseIcon = (props: IconProps) => (
  <svg {...base} {...props}>
    <path d="M4.25 4.25l7.5 7.5M11.75 4.25l-7.5 7.5" />
  </svg>
);

export const ChartLineIcon = (props: IconProps) => (
  <svg {...base} {...props}>
    <path d="M2 2v11.5h12" />
    <path d="M4.6 9.6l2.7-3.2 2.3 2.1 3.6-4.4" />
  </svg>
);

/** A folded map, for the flight map — the plane is the map's subject, not its name. */
export const MapIcon = (props: IconProps) => (
  <svg {...base} {...props}>
    <path d="M2 4l4-2 4 2 4-2v10l-4 2-4-2-4 2Z" />
    <path d="M6 2v10M10 4v10" />
  </svg>
);

export const EyeIcon = (props: IconProps) => (
  <svg {...base} {...props}>
    <path d="M1.4 8s2.4-4.15 6.6-4.15S14.6 8 14.6 8s-2.4 4.15-6.6 4.15S1.4 8 1.4 8Z" />
    <circle cx="8" cy="8" r="1.85" />
  </svg>
);

export const EyeOffIcon = (props: IconProps) => (
  <svg {...base} {...props}>
    <path d="M6.3 4.15A6.7 6.7 0 0 1 8 3.95c4.2 0 6.6 4.05 6.6 4.05a12.4 12.4 0 0 1-2.05 2.55M4.1 5.5A12.1 12.1 0 0 0 1.4 8s2.4 4.05 6.6 4.05c.72 0 1.38-.1 1.98-.28" />
    <path d="M6.7 6.7a1.85 1.85 0 0 0 2.6 2.6" />
    <path d="M2.2 2.2l11.6 11.6" />
  </svg>
);
